/**
 * WorkspaceEvidencePane — Read-only evidence node inspector
 *
 * Surfaces evidence nodes, provenance, temporal snapshots, continuity,
 * inspection state, legal boundary, and chain-of-custody metadata.
 *
 * Gap extension v1: net-new pane; no existing GovernanceWorkspaceShell pane
 * covers evidence nodes as a first-class concept.
 *
 * SAFETY: Read-only, proof-only. No execution authority, no mutation.
 * Not legal advice. Human review required for legal-boundary nodes.
 * Visualization does not imply recommendation or admissibility conclusion.
 */

import React from 'react';
import type { EvidencePaneModel, EvidenceNode } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface WorkspaceEvidencePaneProps {
  model: EvidencePaneModel;
  onSelectNode?: (nodeId: string) => void;
}

function admissibilityColor(status: string) {
  if (status === 'ADMITTED') return SEMANTIC.stabilized;
  if (status === 'REFUSED') return SEMANTIC.refusal;
  return SEMANTIC.neutral;
}

function nodeTypeIcon(nodeType: string): string {
  switch (nodeType) {
    case 'DOCUMENT': return '📄';
    case 'EMAIL': return '✉';
    case 'ARTIFACT': return '◈';
    case 'RECEIPT': return '◉';
    case 'SCREENSHOT': return '▣';
    case 'EXHIBIT': return '⬡';
    default: return '○';
  }
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 600,
      color: NEUTRAL.text500,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      marginBottom: SPACING.sp2,
    }}>
      {title}
    </div>
  );
}

function EvidenceNodeRow({ node, onSelect }: { node: EvidenceNode; onSelect?: (id: string) => void }) {
  const color = admissibilityColor(node.admissibilityStatus);
  return (
    <div
      onClick={() => onSelect?.(node.nodeId)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: SPACING.sp2,
        padding: `${SPACING.sp2}px ${SPACING.sp2}px`,
        backgroundColor: node.isSelected ? NEUTRAL.bg500 : NEUTRAL.bg700,
        borderRadius: 2,
        borderLeft: `2px solid ${node.isSelected ? color.border : 'transparent'}`,
        cursor: onSelect ? 'pointer' : 'default',
        marginBottom: SPACING.sp1,
      }}
      data-testid={`evidence-node-${node.nodeId}`}
    >
      {/* Icon */}
      <span style={{ fontSize: TYPOGRAPHY.label.size, flexShrink: 0, lineHeight: 1.4 }}>
        {nodeTypeIcon(node.nodeType)}
      </span>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: SPACING.sp2 }}>
          <span style={{
            fontSize: TYPOGRAPHY.caption.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {node.label}
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: color.text,
            backgroundColor: color.chip,
            padding: '1px 5px',
            borderRadius: 2,
            textTransform: 'uppercase' as const,
            letterSpacing: 0.3,
            flexShrink: 0,
          }}>
            {node.admissibilityStatus}
          </span>
        </div>

        {/* Source ref */}
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontFamily: TYPOGRAPHY.mono.family,
          color: NEUTRAL.text300,
          marginTop: 2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {node.sourceRef}
        </div>

        {/* Meta row */}
        <div style={{ display: 'flex', gap: SPACING.sp3, marginTop: SPACING.sp1, flexWrap: 'wrap' }}>
          {node.hash && (
            <span style={{ fontSize: TYPOGRAPHY.micro.size, fontFamily: TYPOGRAPHY.mono.family, color: NEUTRAL.text300 }}>
              {node.hash.slice(0, 12)}…
            </span>
          )}
          {node.timestamp && (
            <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
              {node.timestamp.slice(0, 19).replace('T', ' ')}
            </span>
          )}
          {node.legalBoundary && (
            <span style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.refusal.text,
              backgroundColor: SEMANTIC.refusal.chip,
              padding: '0px 4px',
              borderRadius: 2,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              legal boundary
            </span>
          )}
        </div>

        {/* Chain of custody */}
        {node.chainOfCustody.length > 0 && (
          <div style={{ marginTop: SPACING.sp1 }}>
            <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
              custody: {node.chainOfCustody.join(' → ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export const WorkspaceEvidencePane: React.FC<WorkspaceEvidencePaneProps> = ({ model, onSelectNode }) => {
  if (model.error) {
    return (
      <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, fontSize: TYPOGRAPHY.body.size }}>
        Evidence pane error: {model.error}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        backgroundColor: NEUTRAL.bg600,
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.heading.size,
          fontWeight: TYPOGRAPHY.heading.weight,
          color: NEUTRAL.text600,
        }}>
          Evidence
        </span>
        <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
          {model.admittedCount} admitted · {model.pendingCount} pending · {model.refusedCount} refused
        </span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: SPACING.sp3 }}>

        {/* Safety strip */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
          flexWrap: 'wrap',
          marginBottom: SPACING.sp3,
          paddingBottom: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
        }}>
          {['READ-ONLY', 'PROOF-ONLY', 'NOT LEGAL ADVICE'].map(label => (
            <span key={label} style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
              backgroundColor: SEMANTIC.stabilized.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              {label}
            </span>
          ))}
          {model.humanReviewRequired && (
            <span style={{
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              color: SEMANTIC.refusal.text,
              backgroundColor: SEMANTIC.refusal.chip,
              padding: '2px 6px',
              borderRadius: 3,
              textTransform: 'uppercase' as const,
              letterSpacing: 0.3,
            }}>
              HUMAN REVIEW REQUIRED
            </span>
          )}
        </div>

        {/* Stats row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: SPACING.sp2,
          marginBottom: SPACING.sp3,
        }}>
          {[
            { label: 'Total', value: model.totalNodes, color: NEUTRAL.text500 },
            { label: 'Admitted', value: model.admittedCount, color: SEMANTIC.stabilized.text },
            { label: 'Pending', value: model.pendingCount, color: NEUTRAL.text400 },
            { label: 'Legal', value: model.legalBoundaryCount, color: SEMANTIC.refusal.text },
          ].map(stat => (
            <div key={stat.label} style={{
              backgroundColor: NEUTRAL.bg600,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
              padding: `${SPACING.sp2}px`,
              textAlign: 'center' as const,
            }}>
              <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginBottom: 2 }}>{stat.label}</div>
              <div style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Node list */}
        <SectionHeader title={`Evidence Nodes (${model.nodes.length})`} />
        {model.nodes.length === 0 ? (
          <div style={{ padding: SPACING.sp6, color: NEUTRAL.text300, textAlign: 'center' as const }}>
            No evidence nodes
          </div>
        ) : (
          <div>
            {model.nodes.map(node => (
              <EvidenceNodeRow key={node.nodeId} node={node} onSelect={onSelectNode} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};
